'use client';
import { logger, LoggerEvents } from '@bw/core';
import { Button, ButtonProps } from '@bw/elements';
import { LoaderBlue } from '@bw/elements/icons';
import {
    FormResponse
} from '@bw/external-apis';
import { gtmEvent } from '@bw/ui';
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { SubmitHandler, useForm } from 'react-hook-form';
import { BaseForm } from '../base-form/BaseForm';
import { readHiddenInputs } from '../form-helpers';
import styles from './FranchiseRemarketing.module.scss';

const isRequiredErrorMessage = 'This field is required';

export type FranchiseRemarketingForm = {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    question?: string;
    captchaToken: string;
};

export type RequestState =
    | { status: 'ready' | 'loading' | 'success' }
    | {
        status: 'error';
        error: Error;
    };

interface FormProps {
    formId: string;
    formData: {
        name: string;
        id?: string;
        labelText: string;
        inputType: string;
        placeholder?: string | null;
        selectOptions?: string[] | null;
        isRequired: boolean;
        validationRegex?: string;
        validationErrorMessage: string;
    }[];
    buttonData?: ButtonProps;
    formAction?: (
        formId: string,
        data: FranchiseRemarketingForm
    ) => Promise<FormResponse>;
}

const formProps = {
    formId: 'FranchiseRemarketingForm',
    formData: [
        {
            name: 'firstName',
            labelText: 'First Name',
            inputType: 'text',
            placeholder: 'First Name',
            selectOptions: null,
            isRequired: true,
            validationErrorMessage: 'Please enter a valid first name',
        },
        {
            name: 'lastName',
            labelText: 'Last Name',
            inputType: 'text',
            placeholder: 'Last Name',
            selectOptions: null,
            isRequired: true,
            validationErrorMessage: 'Please enter a valid last name',
        },
        {
            name: 'phoneNumber',
            labelText: 'Phone Number',
            inputType: 'tel',
            placeholder: 'Phone Number',
            selectOptions: null,
            isRequired: true,
            validationRegex: /^(\+?[1][-.● ]?)?\(?([2-9][0-9]{2})\)?[-.● ]?([2-9][0-9]{2})[-.● ]?([0-9]{4})$/.source,
            validationErrorMessage: 'Enter a valid phone number in 123-456-7890 format',
        },
        {
            name: 'email',
            labelText: 'Email',
            inputType: 'email',
            placeholder: 'Email',
            selectOptions: null,
            isRequired: true,
            validationRegex:
                /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    .source,
            validationErrorMessage: 'Please enter a valid email',
        },
        {
            name: 'question',
            labelText: 'Do you have any specific questions?',
            inputType: 'text',
            placeholder: 'Do you have any specific questions?',
            selectOptions: null,
            isRequired: false,
            validationErrorMessage: '',
        },
    ],
} as const satisfies FormProps;

interface FranchiseRemarketingClientFormProps {
    formAction: (
        ...args: FranchiseRemarketingForm[]
    ) => Promise<FormResponse>;
}

export function FranchiseRemarketingClientForm({
    formAction,
}: FranchiseRemarketingClientFormProps) {
    if (!formAction) {
        throw new Error('Form action is required');
    }

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<FranchiseRemarketingForm>();
    const [requestState, setRequestState] = useState<RequestState>({
        status: 'ready',
    });
    const [hideForm, setHideForm] = useState<boolean>(false);
    const { executeRecaptcha } = useGoogleReCaptcha();

    const getCaptchaToken = async (
        action: string | undefined
    ): Promise<string> => {
        const message =
            'executeRecaptcha not available on form submit, possible react script loading error.';
        if (!executeRecaptcha) {
            logger.error(LoggerEvents.GeneralErrorWebApp, { message });
            return 'no recaptcha1';
        }
        try {
            const token = await executeRecaptcha(action);
            return token;
        } catch (error) {
            logger.error(LoggerEvents.GeneralErrorWebApp, { message });
        }
        return 'no recaptcha1';
    };

    const onSubmit: SubmitHandler<FranchiseRemarketingForm> = async (
        data,
        event
    ) => {
        if (!event?.target) {
            return;
        }

        // hidden inputs don't trigger the onChange event, so we need to manually read them
        data = { ...data, ...readHiddenInputs(event.target as HTMLFormElement) };

        setRequestState({ status: 'loading' });
        setHideForm(true);

        try {
            const token = await getCaptchaToken(formProps.formId);
            data.captchaToken = token;

            const response = await formAction(data);

            if (typeof response.error === 'string') {
                throw new Error(response.error);
            }

            setRequestState({ status: 'success' });
            gtmEvent('form_success', { formName: formProps.formId });
            reset();
        } catch (error) {
            console.log('caught error');
            if (error instanceof Error) {
                setRequestState({ status: 'error', error });
                setHideForm(false);
            }
        }
    };

    const ErrorDisplay = ({ state }: { state: RequestState }) => {
        return (
            <>
                {state.status === 'error' && (
                    <div className={styles.error}>{state.error.message}</div>
                )}
            </>
        );
    };
    const StateDisplay = ({ state }: { state: RequestState }) => {
        return (
            <>
                {state.status === 'loading' && (
                    <div className="is__pageloader">
                        <figure>
                            <LoaderBlue width={100} height={100} />
                        </figure>
                    </div>
                )}
            </>
        );
    };
    const ThanksDisplay = ({ state }: { state: RequestState }) => {
        return (
            <>
                {state.status === 'success' && (
                    <div className={styles.success}>
                        <h5 className="headingTagline">Submission Successful!</h5>
                        <h2>Thank you!</h2>
                        <p>Someone from our Franchise Sales team will be in touch with you shortly.</p>
                    </div>
                )}
            </>
        );
    };
    const clearErrorState = () => {
        if (requestState.status === 'error') {
            setRequestState({ status: 'ready' });
        }
    };

    return (
        <>
            <div className={styles['formContainer']}>
                {!hideForm ? (
                    <BaseForm
                        formId={formProps.formId}
                        onSubmit={handleSubmit(onSubmit)}
                        onChange={clearErrorState}
                    >
                        {formProps.formData.map((field, index) => {
                            const {
                                name,
                                labelText,
                                inputType,
                                placeholder,
                                selectOptions,
                                isRequired,
                                validationErrorMessage,
                            } = field;
                            const validationRegex =
                                'validationRegex' in field ? field.validationRegex : undefined;
                            const id = field.name;

                            const renderField = () => {
                                const inputDataTestId = labelText
                                    .replace(/\s/g, '-')
                                    .replace(/[^a-zA-Z-]/g, '')
                                    .toLowerCase();

                                const pattern = validationRegex
                                    ? {
                                        value: RegExp(validationRegex),
                                        message: validationErrorMessage,
                                    }
                                    : undefined;
                                switch (inputType) {
                                    case 'text':
                                        return (
                                            <>
                                                <input
                                                    id={id}
                                                    type="text"
                                                    data-testid={inputDataTestId}
                                                    {...register(name, {
                                                        required: isRequired
                                                            ? isRequiredErrorMessage
                                                            : false,
                                                        pattern,
                                                    })}
                                                    placeholder={placeholder ?? ''}
                                                />
                                                <span data-testid={`${inputDataTestId}-error`}>
                                                    {errors[name] ? errors[name].message : ''}
                                                </span>
                                            </>
                                        );
                                    case 'email':
                                        return (
                                            <>
                                                <input
                                                    type="text"
                                                    id={id}
                                                    data-testid={inputDataTestId}
                                                    {...register(name, {
                                                        required: isRequired
                                                            ? isRequiredErrorMessage
                                                            : false,
                                                        pattern,
                                                    })}
                                                    placeholder={placeholder ?? ''}
                                                />
                                                <span data-testid={`${inputDataTestId}-error`}>
                                                    {errors[name] ? errors[name].message : ''}
                                                </span>
                                            </>
                                        );
                                    case 'tel':
                                        return (
                                            <>
                                                <input
                                                    id={id}
                                                    type="tel"
                                                    data-testid={inputDataTestId}
                                                    {...register(name as any, {
                                                        required: isRequired
                                                            ? isRequiredErrorMessage
                                                            : false,
                                                        pattern,
                                                    })}
                                                    placeholder={placeholder ?? ''}
                                                />
                                                <span data-testid={`${inputDataTestId}-error`}>
                                                    {errors[name] ? errors[name].message : ''}
                                                </span>
                                            </>
                                        );
                                    default:
                                        return null;
                                }
                            };
                            return (
                                <div
                                    key={index}
                                    className={styles['formField']}
                                    data-aos="fade-up"
                                >
                                    {/* <label htmlFor={id}>{labelText}</label> */}
                                    {renderField()}
                                </div>
                            );
                        })}

                        <Button
                            id="AgencyReconnectForm-submit"
                            buttonStyle="accent"
                            type="submit"
                            title="Submit"
                            disabled={requestState.status === 'loading'}
                            data-aos="fade-up"
                        />
                        <ErrorDisplay state={requestState} />
                    </BaseForm>
                ) : (
                    <StateDisplay state={requestState} />
                )}
            </div>
            <ThanksDisplay state={requestState} />
        </>
    );
}
